/* KitBrix motion layer: reveals, kinetic headline, hover/scroll product clips, best-seller colour cycle,
   card → product page morph, fly-to-bag, compact header. Everything is skipped
   when the visitor prefers reduced motion. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const root = document.documentElement;
  if (!reduce && 'IntersectionObserver' in window) root.classList.add('kb-motion');

  /* ---------- Card → product page morph (cross-document view transitions) ---------- */
  const VT_KEY = 'kb-vt-product';
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.product-card a[href*="/products/"]');
    if (!link || reduce || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const card = link.closest('.product-card');
    const img = card && card.querySelector('.product-card__img--primary');
    if (!img) return;
    document.querySelectorAll('[style*="kb-product"]').forEach((el) => { el.style.viewTransitionName = ''; });
    img.style.viewTransitionName = 'kb-product';
    try { sessionStorage.setItem(VT_KEY, new URL(link.href, location.href).pathname.split('/products/')[1].split(/[/?#]/)[0]); } catch (_) {}
  }, true);
  // Clear the name if the page is restored from the back/forward cache
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.product-card__img--primary').forEach((el) => { el.style.viewTransitionName = ''; });
  });

  /* ---------- Cookie banner on phones ---------- */
  // 2 Nov (council): on a phone the Consentmo banner covered about half the screen on a first visit. It lives in the
  // app's own shadow root, so theme CSS can't reach it; a few lines of style added inside that root make it a compact
  // bar: smaller text, Accept and Preferences side by side at equal weight. The choices themselves don't change, and
  // if the app's markup changes these rules simply stop applying.
  const compactConsent = (tries = 0) => {
    const shadow = document.querySelector('csm-cookie-consent')?.shadowRoot;
    if (shadow && shadow.querySelector('.cc-window')) {
      if (shadow.querySelector('style[data-kb-compact]')) return;
      const s = document.createElement('style');
      s.dataset.kbCompact = '';
      s.textContent = `@media (max-width: 760px) {
  .csm-cmp-bar .csm-cmp-body { padding: 14px 16px !important; gap: 10px !important; }
  .csm-cmp-bar .cc-message, .csm-cmp-bar .cc-link { font-size: 13px !important; line-height: 1.45 !important; margin: 0 !important; }
  .csm-cmp-bar .cc-link { padding: 0 2px !important; }
  .csm-cmp-bar .cc-compliance { flex-direction: row-reverse !important; gap: 8px !important; }
  .csm-cmp-bar .cc-compliance .cc-btn { flex: 1 1 0 !important; width: auto !important; min-height: 44px !important; margin: 0 !important; }
}`;
      shadow.appendChild(s);
      return;
    }
    if (tries < 40) setTimeout(() => compactConsent(tries + 1), 250);
  };
  compactConsent();

  if (!root.classList.contains('kb-motion')) return;

  /* ---------- Reveals ---------- */
  const REVEAL_GROUPS = [
    '.section-title', '.collection-circles__list > li', '.collection-grid > .product-card', '.collection-grid > .collection-promo-tile',
    '.feature-col', '.article-card', '.usp-item', '.bento-tile', '.tote-card', '.review-card', '.colour-rail__item',
    '.faq-item', '.image-row__item', '.pokit-feature__inner', '.cta-strip__inner', '.check', '.contact-section__inner > *',
    '.rich-text__inner', '.image-with-text__inner > *', '.page-sports__grid > li',
    '.pg-pillar', '.pg-card', '.pg-stat', '.pg-quote', '.pg-partner', '.kb-faq__item'
  ];
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealIO.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  // Items in a sideways-scrolling rail never cross the viewport on their own, so the rail reveals them together
  const railIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.querySelectorAll(':scope > [data-reveal]').forEach((el) => el.classList.add('is-in'));
      railIO.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  const isRail = (el) => !!el && el.scrollWidth > el.clientWidth + 2 && /(auto|scroll)/.test(getComputedStyle(el).overflowX);

  const tagReveals = (scope) => {
    const seen = new Map();
    scope.querySelectorAll(REVEAL_GROUPS.join(',')).forEach((el) => {
      if (el.hasAttribute('data-reveal') || el.closest('.site-header, .site-footer, cart-drawer, .hero-slideshow')) return;
      const parent = el.parentElement;
      const i = seen.get(parent) || 0;
      seen.set(parent, i + 1);
      el.setAttribute('data-reveal', '');
      el.style.setProperty('--reveal-i', Math.min(i, 7));
      const rail = isRail(parent);
      // anything already on screen at load shows straight away (no flash on arrival)
      const r = (rail ? parent : el).getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
      else if (rail) railIO.observe(parent);
      else revealIO.observe(el);
    });
  };

  /* ---------- Kinetic headline ---------- */
  const kinetic = (el) => {
    if (!el || el.dataset.kinetic) return;
    el.dataset.kinetic = '1';
    const words = el.textContent.trim().split(/\s+/);
    el.setAttribute('aria-label', el.textContent.trim());
    el.innerHTML = words.map((w, i) => `<span class="kb-word${i === words.length - 1 ? ' kb-word--mark' : ''}" aria-hidden="true" style="--w:${i}"><span>${w.replace(/</g, '&lt;')}</span></span>`).join(' ');
    el.classList.add('kb-kinetic');
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-in')));
  };

  /* ---------- Product clips ---------- */
  // 2 Nov: clips play by themselves on desktop as well as phones (no hover needed), once ≥60% of the image is
  // on screen. After a play the clip holds its last frame for a moment, fades back to the photo, and plays again
  // 10 s after it finished, for as long as the card stays on screen. It resets when the card scrolls away.
  const CLIP_HOLD_MS = 1500;
  const CLIP_AGAIN_MS = 10000;
  // clips wait for the page's own images, so on a slow phone the banner and first photos arrive first
  const afterLoad = (fn) => { if (document.readyState === 'complete') fn(); else addEventListener('load', () => fn(), { once: true }); };
  const slowNet = () => { const c = navigator.connection; return !!c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || '')); };
  const setupClip = (video) => {
    if (video.dataset.kbReady) return;
    video.dataset.kbReady = '1';
    const card = video.closest('.product-card, .kb-clip-host');
    const box = video.parentElement;
    let visible = false;
    let pending = false;
    let hold = 0;
    let again = 0;
    let resetTimer;

    const swatched = () => card.classList.contains('is-swatched');
    const attach = () => { if (!video.src && video.dataset.src) { video.src = video.dataset.src; } };
    const fadeOut = () => {
      video.classList.remove('is-playing');
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => { video.pause(); try { video.currentTime = 0; } catch (_) {} }, 350);
    };
    const reset = () => {
      clearTimeout(hold); clearTimeout(again); hold = 0; again = 0; pending = false;
      fadeOut();
    };
    const start = () => {
      if (!visible || pending || again || hold || swatched() || document.hidden) return;
      if (!video.paused && !video.ended) return;
      clearTimeout(resetTimer);
      pending = true;
      attach();
      try { if (video.currentTime > 0) video.currentTime = 0; } catch (_) {}
      const p = video.play();
      if (p && p.catch) p.catch(() => { pending = false; });
    };

    video.addEventListener('playing', () => {
      pending = false;
      // the card may have left the screen (or been swatched) before the clip started
      if (!visible || swatched()) { reset(); return; }
      video.classList.add('is-playing');
      card.classList.add('is-clip-on');
    });
    video.addEventListener('ended', () => {
      hold = setTimeout(() => { hold = 0; fadeOut(); }, CLIP_HOLD_MS);
      again = setTimeout(() => { again = 0; start(); }, CLIP_AGAIN_MS);
    });
    video.addEventListener('loadeddata', start);
    video.addEventListener('canplay', start);
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const ratio = entry.intersectionRatio;
        if (ratio >= 0.6) { visible = true; afterLoad(start); }
        else if (ratio < 0.15 && visible) { visible = false; reset(); }
      });
    }, { threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1] }).observe(box);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { if (!video.paused) reset(); } else start();
    });
    // Warm up nearby clips without calling load() (which can abort play on iOS): only once the page has loaded,
    // so clips never compete with the banner for the connection, and not at all on data saver or a slow connection
    if (!slowNet()) afterLoad(() => new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) { attach(); video.preload = 'auto'; obs.disconnect(); }
    }, { rootMargin: '600px 0px' }).observe(box));
  };

  // A clip shows the default colourway, so once a shopper picks another swatch the card stays a still
  document.addEventListener('click', (e) => {
    const dot = e.target.closest('.product-card .swatch-dot');
    if (!dot) return;
    const card = dot.closest('.product-card');
    card.classList.add('is-swatched');
    const v = card.querySelector('.kb-hover-video');
    if (v) { v.classList.remove('is-playing'); v.pause(); }
  });

  /* ---------- Best sellers: the photo fades through each colour while the card isn't hovered (1 Nov) ---------- */
  // Frames and pace come from the card (data-cycle-frames, data-cycle seconds). It pauses on hover, off screen,
  // in a background tab and while a clip plays, and stops for good once a shopper picks a swatch.
  const FADE_MS = 600;
  const setupCycle = (card) => {
    if (card.dataset.kbCycle) return;
    card.dataset.kbCycle = '1';
    let frames;
    try { frames = JSON.parse(card.dataset.cycleFrames || '[]'); } catch (_) { return; }
    const img = card.querySelector('.product-card__img--primary');
    if (!img || frames.length < 2) return;
    const every = Math.max(1, parseFloat(card.dataset.cycle) || 3) * 1000;
    const layer = document.createElement('img');
    layer.className = 'product-card__img product-card__img--cycle';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    img.after(layer);
    const dots = [...card.querySelectorAll('.swatch-dot[data-colour]')];
    let i = 0, timer = 0, busy = false, hovering = false, onScreen = false;

    const mark = () => dots.forEach((d) => d.classList.toggle('is-current', d.dataset.colour === frames[i].c));

    // 2 Nov meeting: each colour photo frames the bag a little differently, so the cycle "jumped". Each photo is
    // reduced to an 80 px outline of the bag (light background left out, thin straps and handles worn away), and the
    // move and scale that best lays it over the first photo's outline is found. The photos themselves are untouched.
    const N = 80;
    const masks = [];
    const maskOf = (k) => masks[k] || (masks[k] = new Promise((done) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => {
        try {
          if (Math.abs(im.naturalWidth / im.naturalHeight - 1) > 0.02) { done(null); return; }
          const c = document.createElement('canvas');
          c.width = N; c.height = N;
          const g = c.getContext('2d', { willReadFrequently: true });
          g.drawImage(im, 0, 0, N, N);
          const d = g.getImageData(0, 0, N, N).data;
          const on = new Uint8Array(N * N), a = new Uint8Array(N * N), m = new Uint8Array(N * N), R = 2;
          for (let p = 0; p < N * N; p++) {
            const lo = Math.min(d[p * 4], d[p * 4 + 1], d[p * 4 + 2]), hi = Math.max(d[p * 4], d[p * 4 + 1], d[p * 4 + 2]);
            on[p] = d[p * 4 + 3] > 128 && (lo < 232 || hi - lo > 24) ? 1 : 0;
          }
          for (let y = 0; y < N; y++) { let run = 0; for (let x = 0; x < N; x++) { run = on[y * N + x] ? run + 1 : 0; if (run > 2 * R) a[y * N + x - R] = 1; } }
          for (let x = 0; x < N; x++) { let run = 0; for (let y = 0; y < N; y++) { run = a[y * N + x] ? run + 1 : 0; if (run > 2 * R) m[(y - R) * N + x] = 1; } }
          done(m);
        } catch (_) { done(null); }
      };
      im.onerror = () => done(null);
      im.src = frames[k].s.replace(/([?&])width=\d+/, '$1width=160');
    }));
    const overlap = (ref, m, s, tx, ty) => {
      let both = 0, either = 0;
      const sx = new Int16Array(N);
      for (let x = 0; x < N; x++) sx[x] = Math.floor((x + 0.5 - N / 2 - tx * N) / s + N / 2);
      for (let y = 0; y < N; y++) {
        const sy = Math.floor((y + 0.5 - N / 2 - ty * N) / s + N / 2);
        const row = sy >= 0 && sy < N ? sy * N : -1;
        for (let x = 0; x < N; x++) {
          const r = ref[y * N + x], v = row >= 0 && sx[x] >= 0 && sx[x] < N ? m[row + sx[x]] : 0;
          both += r & v; either += r | v;
        }
      }
      return either ? both / either : 0;
    };
    const fits = [];
    const fitOf = (k) => fits[k] || (fits[k] = (async () => {
      if (k === 0) return null;
      const [ref, m] = await Promise.all([maskOf(0), maskOf(k)]);
      if (!ref || !m) return null;
      let best = [overlap(ref, m, 1, 0, 0), 1, 0, 0];
      const look = (s, tx, ty) => { const v = overlap(ref, m, s, tx, ty); if (v > best[0]) best = [v, s, tx, ty]; };
      for (let s = 0.82; s < 1.19; s += 0.03) for (let tx = -0.12; tx < 0.121; tx += 0.02) for (let ty = -0.12; ty < 0.121; ty += 0.02) look(s, tx, ty);
      const [, s0, x0, y0] = best;
      for (let s = s0 - 0.03; s < s0 + 0.031; s += 0.01) for (let tx = x0 - 0.02; tx < x0 + 0.021; tx += 0.005) for (let ty = y0 - 0.02; ty < y0 + 0.021; ty += 0.005) look(s, tx, ty);
      const [, s1, tx1, ty1] = best;
      return s1 === 1 && tx1 === 0 && ty1 === 0 ? null : { s: s1.toFixed(3), t: `${(tx1 * 100).toFixed(2)}% ${(ty1 * 100).toFixed(2)}%` };
    })());
    const place = (el, fit) => { el.style.scale = fit ? fit.s : ''; el.style.translate = fit ? fit.t : ''; };
    const stopped = () => card.classList.contains('is-swatched');
    const paused = () => hovering || !onScreen || document.hidden || !!card.querySelector('.kb-hover-video.is-playing');
    const schedule = (ms = every) => { clearTimeout(timer); if (!stopped()) timer = setTimeout(step, ms); };
    const preload = (k) => { const pre = new Image(); pre.src = frames[k % frames.length].s; };

    const step = async () => {
      timer = 0;
      if (stopped() || paused()) return;
      if (busy) { schedule(200); return; }
      busy = true;
      const next = (i + 1) % frames.length;
      const fit = await fitOf(next);
      layer.src = frames[next].s;
      place(layer, fit);
      try { await layer.decode(); } catch (_) { busy = false; schedule(); return; }
      if (stopped() || hovering) { busy = false; return; }
      schedule();
      layer.classList.add('is-on');
      i = next;
      mark();
      card.dataset.cycleAt = String(Math.round(performance.now()));
      await new Promise((r) => setTimeout(r, FADE_MS));
      // hand the new photo to the card itself, then drop the layer without a flash
      img.removeAttribute('srcset');
      img.src = frames[i].s;
      try { await img.decode(); } catch (_) {}
      place(img, fit);
      requestAnimationFrame(() => {
        layer.classList.remove('is-on');
        busy = false;
        if (!timer && !paused()) schedule();
      });
      preload(i + 1);
    };

    const pause = () => { hovering = true; clearTimeout(timer); timer = 0; };
    const resume = () => { hovering = false; if (!busy) schedule(); };
    card.addEventListener('mouseenter', pause);
    card.addEventListener('mouseleave', resume);
    card.addEventListener('focusin', pause);
    card.addEventListener('focusout', (e) => { if (!card.contains(e.relatedTarget)) resume(); });
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        onScreen = entry.isIntersecting;
        if (onScreen) { if (!timer && !busy) schedule(); } else { clearTimeout(timer); timer = 0; }
      });
    }, { threshold: 0.35 }).observe(img);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && onScreen && !timer && !busy) schedule(); });
    card.addEventListener('click', (e) => { if (e.target.closest('.swatch-dot')) { clearTimeout(timer); place(img, null); place(layer, null); dots.forEach((d) => d.classList.toggle('is-current', d === e.target.closest('.swatch-dot'))); } });
    mark();
    preload(1);
  };

  /* ---------- Fly-to-bag ---------- */
  const cartIcon = () => document.querySelector('.site-header__cart-trigger');
  const fly = (fromImg) => {
    const target = cartIcon();
    if (!fromImg || !target) return;
    const a = fromImg.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    if (!a.width || a.bottom < 0 || a.top > window.innerHeight) return;
    const ghost = document.createElement('img');
    ghost.src = fromImg.currentSrc || fromImg.src;
    ghost.className = 'kb-fly';
    ghost.alt = '';
    const size = Math.min(a.width, 160);
    Object.assign(ghost.style, { left: `${a.left + (a.width - size) / 2}px`, top: `${a.top + (a.height - size) / 2}px`, width: `${size}px`, height: `${size}px` });
    document.body.appendChild(ghost);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const anim = ghost.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 60}px) scale(0.55)`, opacity: 0.95, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0.2 }
    ], { duration: 720, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' });
    anim.onfinish = () => {
      ghost.remove();
      target.classList.remove('is-nudge'); void target.offsetWidth; target.classList.add('is-nudge');
      document.querySelectorAll('[data-cart-count]').forEach((c) => { c.classList.remove('is-pop'); void c.offsetWidth; c.classList.add('is-pop'); });
    };
  };
  document.addEventListener('click', (e) => {
    const quick = e.target.closest('[data-quick-add-id]');
    if (quick && !quick.disabled) {
      const host = quick.closest('.product-card, .colour-rail__item, .addon-card');
      fly(host && host.querySelector('img'));
    }
  }, true);
  document.addEventListener('submit', (e) => {
    if (e.target.closest && e.target.closest('product-form')) fly(document.getElementById('ProductMainImage'));
  }, true);

  /* ---------- Header compacts after scrolling ---------- */
  const header = document.querySelector('.site-header');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 80); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Hero slideshow: mark the active slide for the push-in ---------- */
  const syncHero = () => {
    document.querySelectorAll('.hero-slideshow').forEach((show) => {
      const track = show.querySelector('[data-track]');
      if (!track) return;
      show.style.setProperty('--kb-slide-ms', `${(parseInt(show.dataset.autoplay, 10) || 5) * 1000}ms`);
      const mark = () => {
        const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        [...track.children].forEach((s, k) => s.classList.toggle('is-active', k === i));
      };
      mark();
      track.addEventListener('scroll', () => { clearTimeout(track._kb); track._kb = setTimeout(mark, 90); }, { passive: true });
    });
  };

  /* ---------- Reading progress on stories ---------- */
  const progress = () => {
    if (!document.body.classList.contains('template-article')) return;
    const bar = document.createElement('div');
    bar.className = 'kb-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    if (!CSS.supports('animation-timeline: scroll()')) {
      const upd = () => { const h = document.documentElement.scrollHeight - innerHeight; bar.style.transform = `scaleX(${h > 0 ? scrollY / h : 0})`; };
      addEventListener('scroll', upd, { passive: true }); upd();
    }
  };

  const init = () => {
    tagReveals(document);
    kinetic(document.querySelector('.hero-feature__heading') || document.querySelector('.usp-bar__heading'));
    document.querySelectorAll('video.kb-hover-video').forEach(setupClip);
    document.querySelectorAll('.product-card[data-cycle-frames]').forEach(setupCycle);
    syncHero();
    progress();
    // Sections that render later (recommendations, cart refresh) get the same treatment
    new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        tagReveals(n);
        n.querySelectorAll && n.querySelectorAll('video.kb-hover-video').forEach(setupClip);
        n.querySelectorAll && n.querySelectorAll('.product-card[data-cycle-frames]').forEach(setupCycle);
      }));
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
